import React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "@/shared/ui/Blocks";
import { LayoutWithResizableColumns } from "@/shared/widgets/Layout";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { TabNavigation } from "@/shared/widgets/TabNavigation";
import ProfileSectionProvider, {
  useProfileSection,
} from "./ProfileSectionContext";
import GatewayChat, { GatewayChatProps } from "@/shared/widgets/GatewayChat";

interface ProfileLayoutContentProps {
  children: React.ReactNode;
  isReady?: boolean;
  loadingMessage?: string;
  scrollContainerId?: string;
}

interface ProfileLayoutProps extends GatewayChatProps {
  children: React.ReactNode;
  loadingMessage?: string;
}

export function ProfileLayoutContent(props: ProfileLayoutContentProps) {
  const {
    children,
    isReady,
    loadingMessage,
    scrollContainerId = "column-0",
  } = props;

  const { t } = useTranslation();
  const { sections } = useProfileSection();

  const navigationTabs = sections.map((tab) => ({
    ...tab,
    path: `#${tab.id}`,
  }));
  return (
    <Column className="flex-1 relative">
      <TabNavigation
        isInpage
        tabs={navigationTabs}
        className="px-8 sticky top-0 bg-white z-30"
        scrollContainerId={scrollContainerId}
      />
      <ScreenContainer
        id="profileContent"
        loading={!isReady}
        loadingMessage={loadingMessage ?? t("common.loading_profile")}
      >
        <Column className="flex-1 px-8 md:px-8 gap-16 pb-24">{children}</Column>
      </ScreenContainer>
    </Column>
  );
}

function ProfileLayout(props: ProfileLayoutProps) {
  const { children, isReady, loadingMessage, ...aiChatProps } = props;

  return (
    <ProfileSectionProvider>
      <LayoutWithResizableColumns
        columnsGrow={[70, 30]}
        columnsMinSize={[50, 15]}
      >
        <ProfileLayoutContent isReady={isReady} loadingMessage={loadingMessage}>
          {children}
        </ProfileLayoutContent>
        <Column className="h-full w-full" id="wisgpt-container">
          {!!aiChatProps?.scope && (
            <GatewayChat isReady={isReady} {...aiChatProps} />
          )}
        </Column>
      </LayoutWithResizableColumns>
    </ProfileSectionProvider>
  );
}

export default ProfileLayout;
